import { memo } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Server, Monitor, Wifi, HardDrive, Printer, Shield, Router, type LucideIcon, Box, X, Cctv, EthernetPort, Radio } from 'lucide-react';
import { useHoverStore } from '../../../utils/store';

const getAssetIcon = (type: string): LucideIcon => {
    const icons: Record<string, LucideIcon> = {
        camera: Cctv, server: Server, workstation: Monitor, access_point: Radio,
        nvr: HardDrive, printer: Printer, firewall: Shield, router: Router,
        switch: Router, storage: HardDrive, other: Box, local_asset: Server, isp: Wifi,
        hub: EthernetPort, // Added hub for unmanaged switches
    };
    return icons[type] || Box;
};

const AssetNode = memo(({ id, data, isConnectable }: NodeProps<any>) => {
    const { assetName, assetType, onDelete, assetIp, status, hasProblem } = data;
    // console.log(data);
    // console.log(status);
    const Icon = getAssetIcon(assetType);

    const hoveredAssetId = useHoverStore((state) => state.hoveredAssetId);
    const setNodeHovered = useHoverStore((state) => state.setNodeHovered);

    const isHighlighted = hoveredAssetId === id;

    return (
        <div
            className="relative group"
            onMouseEnter={() => setNodeHovered(id)}
            onMouseLeave={() => setNodeHovered(null)}
        >
            {!data?.readOnly && onDelete && (
                <button
                    onClick={(e) => { e.stopPropagation(); onDelete?.(id); }}
                    className="absolute -top-3 -right-3 z-50 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all hover:bg-red-600 hover:scale-110"
                >
                    <X size={10} strokeWidth={3} />
                </button>
            )}

            <div
                className={`border rounded-xl px-4 py-2.5 flex items-center gap-3 shadow-xl cursor-pointer transition-all duration-300 ${(((status === 'in_active' || status === 'offline' || hasProblem) && assetType !== 'unmanaged_switch') || hasProblem)
                    ? 'bg-red-500/10 dark:bg-red-600/20 border-red-500 dark:border-red-400 shadow-red-500/40 animate-pulse'
                    : isHighlighted
                        ? 'bg-blue-50 dark:bg-blue-600/20 border-blue-500 dark:border-blue-400 shadow-blue-500/50 scale-110 ring-4 ring-blue-500/20'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 shadow-md'
                    }`}
            >
                <Icon
                    className={`w-7 h-7 flex-shrink-0 ${status === 'in_active' || hasProblem || status === 'offline'
                        ? 'text-red-500 dark:text-red-400 animate-pulse'
                        : assetType === 'unmanaged_switch'
                            ? 'text-blue-600 dark:text-blue-400'
                            : isHighlighted
                                ? 'text-green-600 dark:text-green-300'
                                : 'text-green-600 dark:text-green-400'
                        }`}
                />

                {/* Text Container: Name on top, IP on bottom */}
                <div className="flex flex-col justify-center min-w-0">
                    <span className={`${isHighlighted ? 'text-blue-600 dark:text-white font-bold' : 'text-gray-800 dark:text-gray-200'} text-xs font-semibold max-w-[120px] truncate leading-tight`}>
                        {assetName}
                    </span>

                    {assetType !== 'hub' && (
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono mt-0.5 truncate max-w-[120px] leading-tight">
                            {assetIp}
                        </span>
                    )}
                </div>
            </div>

            {/* Top Handles */}
            <Handle
                type="target"
                position={Position.Top}
                id="top"
                isConnectable={isConnectable}
                className="!w-2.5 !h-2.5 !bg-blue-500 !border-2 !border-white dark:!border-gray-900 !rounded-full transition-transform hover:scale-125"
                style={{ top: -5 }}
            />
            <Handle
                type="source"
                position={Position.Top}
                id="top-source"
                isConnectable={isConnectable}
                className="opacity-0"
                style={{ top: -5 }}
            />

            {/* Bottom Handles */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="bottom"
                isConnectable={isConnectable}
                className="!w-2.5 !h-2.5 !bg-green-500 !border-2 !border-white dark:!border-gray-900 !rounded-full transition-transform hover:scale-125"
                style={{ bottom: -5 }}
            />
            <Handle
                type="target"
                position={Position.Bottom}
                id="bottom-target"
                isConnectable={isConnectable}
                className="opacity-0"
                style={{ bottom: -5 }}
            />

            {/* Left Handles */}
            <Handle
                type="target"
                position={Position.Left}
                id="left"
                isConnectable={isConnectable}
                className="!w-2.5 !h-2.5 !bg-blue-500 !border-2 !border-white dark:!border-gray-900 !rounded-full transition-transform hover:scale-125"
                style={{ left: -5 }}
            />
            <Handle
                type="source"
                position={Position.Left}
                id="left-source"
                isConnectable={isConnectable}
                className="opacity-0"
                style={{ left: -5 }}
            />

            {/* Right Handles */}
            <Handle
                type="source"
                position={Position.Right}
                id="right"
                isConnectable={isConnectable}
                className="!w-2.5 !h-2.5 !bg-green-500 !border-2 !border-white dark:!border-gray-900 !rounded-full transition-transform hover:scale-125"
                style={{ right: -5 }}
            />
            <Handle
                type="target"
                position={Position.Right}
                id="right-target"
                isConnectable={isConnectable}
                className="opacity-0"
                style={{ right: -5 }}
            />
        </div>
    );
});

AssetNode.displayName = 'AssetNode';
export default AssetNode;
